import type { ReactElement } from 'react';
import React, { useCallback } from 'react';
import type { Filters, FormValues } from 'pages/clusters/types';
import { Toggle } from 'pages/reports/components/shared-atom-components/filters';
import { ROLE } from 'services/assist-api/entities/role/roles';
import { useRoleMatch } from 'services/providers/auth.hooks';
import EthnicityFilter from './ethnicity-filter';
import GenderFilter from './gender-filter';
import OrganisationFilter from './organisation-filter';
import YearLevelFilter from './year-level-filter';

type Props = {
  onChange(arg: Filters): void;
  entryFilters: FormValues | undefined;
  filters: Filters | undefined;
  isDisabled?: boolean;
};

/**
 * Sidebar filters for cluster reporting
 */
export default function FilterSidebar({
  onChange,
  filters,
  entryFilters,
  isDisabled
}: Props): ReactElement {
  const handleChange = useCallback(
    (changedProperties: Partial<Filters>) => onChange({ ...filters, ...changedProperties }),
    [filters, onChange]
  );

  // Ensure that user has a 'Cluster Admin' or higher role in order to be able to filter by customer/organisation.
  const isClusterAdmin = useRoleMatch([ROLE.CUSTOMER_GROUP_LEADER]);

  return (
    <>
      {isClusterAdmin && (
        <OrganisationFilter
          filters={filters}
          entryFilters={entryFilters}
          value={filters?.clusterCustomers}
          onChange={(clusterCustomers): void => handleChange({ clusterCustomers })}
          isDisabled={isDisabled}
          isMulti
        />
      )}
      <GenderFilter
        filters={filters}
        entryFilters={entryFilters}
        value={filters?.genders}
        onChange={(genders): void => handleChange({ genders })}
        isDisabled={isDisabled}
        isMulti
      />
      <EthnicityFilter
        filters={filters}
        entryFilters={entryFilters}
        value={filters?.ethnicities}
        onChange={(ethnicities): void => handleChange({ ethnicities })}
        isDisabled={isDisabled}
      />
      <YearLevelFilter
        filters={filters}
        entryFilters={entryFilters}
        value={filters?.levels}
        onChange={(levels): void => handleChange({ levels })}
        isDisabled={isDisabled}
        isMulti
      />
      <Toggle
        label="Hide reference data"
        value={!!filters?.isReferenceDataHidden}
        onChange={(isReferenceDataHidden): void => handleChange({ isReferenceDataHidden })}
        isDisabled={isDisabled}
      />
    </>
  );
}
